import { Button, Spacer, Stack } from '@pypestream/design-system';
import { FC, useMemo, useState } from 'react';

import { ChannelSettingsTemplateSections } from '../components/channel-settings-form/channel-settings-template-sections';
import {
  findAssignedRuleSetId,
  findBoundConnectionIds,
  toPlatformConnectionOptions,
  useChannelTemplateData,
} from '../components/channel-settings-form/channel-template-bindings';
import { useIsMountedRef, useProStudioViewerRole } from '../hooks';
import type { Channel } from '../utils';
import { resolveMaskingAssignment } from '../utils/masking-rule-set-options';
import { proStudioService, sendProStudioEvent } from '../xstate/app.xstate';
import { waitForProStudioIdle } from '../xstate/wait-for-pro-studio-idle';

/**
 * A channel's Live Agent Platform, Reporting API and Auto-masking bindings in
 * the Design stage's channel panel — the prototype's `platformSections`
 * (`prostudio-build-flow-layouts.html`). The platforms, credentials and rule
 * sets themselves are project-level and managed in Project Settings; this only
 * shows what the channel is bound to and changes the binding, through the same
 * `assignPypeConnection` / `assignPypeRuleGroup` flows as Channel Settings.
 */
export const DesignChannelConnections: FC<{ channel: Channel }> = ({
  channel,
}) => {
  const isViewer = useProStudioViewerRole();
  const { pypeId, env } = channel;
  const [saving, setSaving] = useState(false);
  // Armed in the effect's SETUP (see the hook), so React 18's StrictMode replay
  // cannot leave it `false`; read by `save` after each wait for the machine.
  const isMountedRef = useIsMountedRef();
  const {
    pypeRuleGroups,
    pypeConnections,
    credentials,
    ruleGroupsLoaded,
    ruleGroupsFailed,
    liveAgentLoaded,
    liveAgentFailed,
  } = useChannelTemplateData({ enabled: true, pypeId, env, isSaving: saving });

  const initialRuleSetId = useMemo(
    () => findAssignedRuleSetId(pypeRuleGroups, pypeId, env),
    [pypeRuleGroups, pypeId, env]
  );
  // Single-select, like Channel Settings: it shows (and switches) the first.
  const initialConnectionId = useMemo(
    () => findBoundConnectionIds(pypeConnections, pypeId, env)[0] ?? '',
    [pypeConnections, pypeId, env]
  );
  const platformConnections = useMemo(
    () => toPlatformConnectionOptions(pypeConnections, env),
    [pypeConnections, env]
  );

  // `null` = untouched (mirror the persisted value), as in Channel Settings.
  const [pickedRuleSetId, setPickedRuleSetId] = useState<string | null>(null);
  const [pickedConnectionId, setPickedConnectionId] = useState<string | null>(
    null
  );

  // PSTUDIO-4051: the select shows a channel bound to the project's SYSTEM rule
  // set as the empty-valued "(Default)" option, so the dirty-check must compare
  // normalised with normalised — `resolveMaskingAssignment` does that, while its
  // payload ids stay RAW (see its docblock). Same wiring as channel-settings-form.
  const maskingAssignment = resolveMaskingAssignment(
    pypeRuleGroups ?? [],
    initialRuleSetId,
    pickedRuleSetId
  );
  const ruleSetId = maskingAssignment.selectValue;
  const connectionId = pickedConnectionId ?? initialConnectionId;
  // Until the rule sets are in, `initialRuleSetId` is '' for every channel, so a
  // pick would look dirty against a binding we cannot see yet: no data, no dirty.
  const maskingTouched = pypeRuleGroups != null && maskingAssignment.touched;
  const lapTouched = connectionId !== initialConnectionId;

  const reset = () => {
    setPickedRuleSetId(null);
    setPickedConnectionId(null);
  };

  const save = async () => {
    setSaving(true);
    const assignments: Array<() => void> = [];
    if (maskingTouched) {
      assignments.push(() =>
        sendProStudioEvent({
          type: 'assignPypeRuleGroup',
          payload: {
            pypeId,
            env,
            fromGroupId: maskingAssignment.fromGroupId,
            toGroupId: maskingAssignment.toGroupId,
          },
        })
      );
    }
    if (lapTouched) {
      assignments.push(() =>
        sendProStudioEvent({
          type: 'assignPypeConnection',
          payload: {
            pypeId,
            env,
            fromConnectionId: initialConnectionId || undefined,
            toConnectionId: connectionId || undefined,
          },
        })
      );
    }

    // Both flows are handled only in the machine's `idle` state
    // (PSTUDIO-3897), so each waits for it. Waiting again after the last one
    // also covers a failed flow, which returns to `idle` via `botFlowError`
    // (and toasts its own error) without calling back.
    //
    // The wait can outlive this panel, and `proStudioService` is a module
    // singleton, so a dispatch queued behind it would still fire for a binding
    // change nobody is looking at any more. Each dispatch therefore re-checks the
    // mounted ref (`save-abandoned`, as `channel-settings-form` does).
    //
    // The panel DOES go away mid-save in ordinary use: `design-channel-panel.tsx`
    // mounts this block only while that channel's Connections accordion is open
    // (one `openConnectionsChannel` for the whole panel), so collapsing it, or
    // opening Connections of another channel, right after Save unmounts the form
    // while `save` is parked. The abandoned save must not be silent, so the catch
    // below reports it through `sendProStudioEvent({ type: 'showError' })` — an
    // app-level toast that does not depend on this component — and skips only the
    // local `reset`/`setSaving`, which have no mounted state left to update.
    // Limitation: a panel that unmounts BETWEEN two assignments has already sent
    // the first one and drops only the second; the message then says "could not
    // be saved" about a save that was half applied, and nothing undoes the half.
    try {
      for (const dispatch of assignments) {
        // eslint-disable-next-line no-await-in-loop
        await waitForProStudioIdle(proStudioService);
        if (!isMountedRef.current) {
          throw new Error('save-abandoned');
        }
        dispatch();
      }
      await waitForProStudioIdle(proStudioService);
    } catch {
      sendProStudioEvent({
        type: 'showError',
        error: 'Your changes could not be saved. Please try again.',
      });
      // An abandoned save has no panel left to reset.
      if (!isMountedRef.current) {
        return;
      }
    }
    // The flows write the new bindings back into context, so the "initial"
    // values now show what was actually saved.
    reset();
    setSaving(false);
  };

  return (
    <div
      className="c-design-channel-connections"
      data-testid="bobcat-design-channel-connections"
    >
      <ChannelSettingsTemplateSections
        ruleSets={pypeRuleGroups ?? []}
        ruleSetsLoaded={ruleGroupsLoaded}
        ruleSetsLoadFailed={ruleGroupsFailed}
        selectedRuleSetId={ruleSetId}
        onRuleSetChange={setPickedRuleSetId}
        platformConnections={platformConnections}
        platformConnectionsLoaded={liveAgentLoaded}
        platformConnectionsLoadFailed={liveAgentFailed}
        selectedConnectionId={connectionId}
        onSelectConnection={setPickedConnectionId}
        credentials={credentials}
        isViewer={isViewer}
        compact
      />
      {(maskingTouched || lapTouched) && (
        <>
          <Spacer size="small" />
          <Stack justifyContent="end" gutter="xsmall">
            <Button
              variant="secondary"
              size="small"
              disabled={saving}
              onClick={reset}
            >
              Cancel
            </Button>
            <Button
              size="small"
              test-id="bobcat-design-channel-connections-save"
              disabled={saving}
              onClick={save}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </>
      )}
    </div>
  );
};
